import { format } from 'date-fns'
import { Tabs } from 'component-library'
import { useCheckBanner } from 'shared-utilities'
import Seo from '../../components/seo'
import { useEffect, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import DatasetHero from '../../components/DatasetHero/DatasetHero'
import DatasetBreadcrumbs from '../../components/DatasetBreadcrumbs/DatasetBreadcrumbs'
import DatasetDataTab from '../../components/DatasetDataTab/DatasetDataTab'
import DatasetLearningMaterials from '../../components/DatasetLearningMaterials/DatasetLearningMaterials'
import DatasetSourceCode from '../../components/DatasetSourceCode/DatasetSourceCode'
import DatasetAboutTab from '../../components/DatasetAboutTab/DatasetAboutTab'
import DatasetSampleTab from '../../components/DatasetSampleTab/DatasetSampleTab'
import { DatasetContext } from '../../context'
import { QCHEM_RADIO_BUTTONS } from '../../components/DownloadForm/helpers'
import RelatedCarousel from '../../components/RelatedCarousel/RelatedCarousel'
import datasetContent from '../../../content/dataset'
import { PERIODICITY_RADIO_BUTTONS } from '../../components/ConfigureForm/helpers'
import { formatDataFamilySlug } from '../../utils/link_actions'
import { graphql } from 'gatsby'

import './style.scss'

const { relatedContent } = datasetContent

export const query = graphql`
  query GetDatasetsFamiliesData($slug: String!) {
    datasets {
      datasetFamily(slug: $slug) {
        authorNames
        authors {
          avatarUrl
          firstName
          handle
          about
          headline
          lastName
        }
        changelog
        citation
        description
        heroImage
        license
        modifiedAt
        previewImage
        publishedAt
        slug
        sourceCode
        tags
        title
        usingThisDataset
        class {
          id
          title
          attributes {
            name
          }
          parameters {
            name
          }
        }
        collection {
          slug
          title
        }
        features {
          content
          title
          type
        }
        parameterOptions {
          options
          selection
        }
      }
    }
  }
`

export default function DatasetPage({
  data,
}: {
  data: Queries.GetDatasetsFamiliesDataQuery
}) {
  const family = data.datasets.datasetFamily
  if (family === null) {
    throw new Error('No family with slug')
  }

  const hasParameters = family.class.parameters.length > 0
  const firstParameterName = family.class.parameters[0]?.name
  const downloadName = firstParameterName
    ? family.parameterOptions.selection[firstParameterName]
    : family.slug

  const attributeNames = family.class.attributes.map(
    (attribute) => attribute.name
  )
  const [selectedAttributes, setSelectedAttributes] =
    useState<string[]>(attributeNames)

  const [selectedBondAngle, setSelectedBondAngle] = useState<string>(
    family.parameterOptions.selection['bondangle']
  )
  const [selectedBondlength, setSelectedBondlength] = useState<number>(
    Number(family.parameterOptions.selection['bondlength'])
  )
  const [selectedBasis, setSelectedBasis] = useState<string>(
    family.parameterOptions.selection['basis']
  )
  const [selectedPeriodicity, setSelectedPeriodicity] = useState<string>(
    PERIODICITY_RADIO_BUTTONS[0].value
  )
  const [selectedLattice, setSelectedLattice] = useState<string>(
    family.parameterOptions.selection['lattice']
  )
  const [selectedLayout, setSelectedLayout] = useState<string>(
    family.parameterOptions.selection['layout']
  )
  const [selectedConfiguration, setSelectedConfiguration] = useState<string>(
    QCHEM_RADIO_BUTTONS[0].value
  )
  const [selectedNumSpinOrbitals, setSelectedNumSpinOrbitals] =
    useState<string>(
      family.parameterOptions.selection['number_of_spin_orbitals']
    )

  const [parameterDefaults, setParameterDefaults] = useState<{
    options: { [key: string]: string[] }
    selection: { [key: string]: string }
  }>(family.parameterOptions)
  const [parameterOptions, setParameterOptions] = useState<{
    options: { [key: string]: string[] }
    selection: { [key: string]: string }
  }>(family.parameterOptions)
  const [tabsList, setTabsList] = useState<
    {
      label: string
      disabled: boolean
      content: JSX.Element
    }[]
  >([
    {
      label: 'About',
      disabled: false,
      content: (
        <DatasetAboutTab
          tags={family.tags}
          authorNames={family.authorNames}
          authors={family.authors}
          changelog={family.changelog}
          citation={family.citation}
          license={family.license}
          usingThisDataset={family.usingThisDataset}
          updated={family.modifiedAt}
        />
      ),
    },
    {
      label: 'Data',
      disabled: false,
      content: <DatasetDataTab features={family.features} />,
    },
    {
      label: 'Samples',
      disabled: false,
      content: <DatasetSampleTab features={family.features} />,
    },
    {
      label: 'Source code',
      disabled: false,
      content: (
        <DatasetSourceCode
          slug={family.class.id}
          sourceCodeLink={family.sourceCode}
        />
      ),
    },
    {
      label: 'Learning materials',
      disabled: false,
      content: <DatasetLearningMaterials />,
    },
  ])

  const [isBannerPresent, setIsBannerPresent] = useState<boolean>(false)

  useCheckBanner(setIsBannerPresent)

  useEffect(() => {
    if (family.features.every((feature) => feature.type !== 'SAMPLES')) {
      setTabsList((tabsList) =>
        tabsList.filter((tab) => tab.label !== 'Samples')
      )
    }
    if (!family.sourceCode) {
      setTabsList((tabsList) =>
        tabsList.filter((tab) => tab.label !== 'Source code')
      )
    }
  }, [family.features, family.slug, family.sourceCode])

  return (
    <DatasetContext.Provider
      value={{
        attributeNames,
        selectedAttributes,
        setSelectedAttributes,
        selectedBondlength,
        setSelectedBondlength,
        selectedBondAngle,
        setSelectedBondAngle,
        selectedBasis,
        setSelectedBasis,
        selectedPeriodicity,
        setSelectedPeriodicity,
        selectedLattice,
        setSelectedLattice,
        selectedLayout,
        setSelectedLayout,
        selectedNumSpinOrbitals,
        setSelectedNumSpinOrbitals,
        selectedConfiguration,
        setSelectedConfiguration,
        parameterDefaults,
        setParameterDefaults,
        pageSlug: family.slug,
        parameterOptions,
        setParameterOptions,
        downloadName,
        modifiedAt: family.modifiedAt,
      }}
    >
      <DatasetBreadcrumbs
        name={family.title}
        categoryByCategorySlug={
          family.collection
            ? {
                name: family.collection.title,
                url: `/collection/${family.collection.slug}`,
              }
            : undefined
        }
        className={`Dataset__breadcrumbs ${
          isBannerPresent ? 'Dataset__breadcrumbs--bannerPresent' : ''
        }`}
      />
      <div className="DatasetPage">
        <DatasetHero
          title={family.title}
          breadcrumbCategory={
            family.collection
              ? {
                  name: family.collection.title,
                  url: `/collection/${family.collection.slug}`,
                }
              : undefined
          }
          categorySlug={family.class.id}
          hasParameters={hasParameters}
          heroImage={
            !family.heroImage || !family.heroImage.includes('https://')
              ? 'https://assets.cloud.pennylane.ai/datasets/generic/hero/pennylane-datasets-hero-generic.png'
              : family.heroImage
          }
        />
        <div>
          <Tabs tabs={tabsList} />
        </div>
      </div>
      <div className="RelatedContainer">
        <RelatedCarousel
          relatedItems={relatedContent}
          title="Related content"
          showCarousel={true}
        />
      </div>
      <ToastContainer
        hideProgressBar={true}
        position="top-center"
        autoClose={1500}
      />
    </DatasetContext.Provider>
  )
}

export const Head = ({
  data,
}: {
  data: Queries.GetDatasetsFamiliesDataQuery
}) => {
  const family = data.datasets.datasetFamily
  if (family === null) {
    throw new Error('Failed to fetch dataset data')
  }

  return (
    <>
      <Seo
        title={family.title}
        image={family.previewImage}
        description={family.description}
      />
      <meta
        name="citation_title"
        content={`PennyLane Data for ${family.title}`}
      />
      <meta name="citation_abstract" content={family.description} />
      {family.authorNames.map((author, index) => (
        <meta
          key={`citation-author-${index}`}
          name="citation_author"
          content={author}
        />
      ))}
      <meta
        name="citation_publication_date"
        content={format(new Date(family.publishedAt), 'yyyy-MM-dd')}
      />
      <meta name="citation_publisher" content="PennyLane" />
      <meta name="citation_journal_title" content="PennyLane Quantum Data" />
      <link
        rel="preload"
        as="image"
        href={family.heroImage}
        // fetchpriority is supported in TypeScript via custom.d.ts declaration its a valid attribute just not supported by the type system.
        // eslint-disable-next-line react/no-unknown-property
        fetchpriority="high"
      />
      <script type="application/ld+json">
        {JSON.stringify([
          {
            '@context': 'https://schema.org',
            '@type': 'Dataset',
            name: `${family.title} data for quantum computing`,
            description: family.description,
            alternateName: family.title,
            creator: family.authorNames.map((author) => ({
              '@type': 'Person',
              name: author,
            })),
            license: 'http://creativecommons.org/licenses/by-sa/4.0/',
            citation: family.citation,
            keywords: family.tags.map((tag) => tag).join(', '),
            url: `https://pennylane.ai/datasets/${
              family.class.id
            }/${formatDataFamilySlug(family.slug)}`,
            version: family.changelog,
            funder: {
              '@type': 'Organization',
              sameAs: 'https://www.xanadu.ai/',
              name: 'Xanadu Quantum Technologies',
            },
            isAccessibleForFree: true,
            measurementTechnique: 'Simulation',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ScholarlyArticle',
            headline: `${family.title} data for quantum computing`,
            image: family.previewImage,
            description: family.description,
            datePublished: family.publishedAt,
            dateModified: family.modifiedAt,
            license: 'http://creativecommons.org/licenses/by-sa/4.0/',
            isAccessibleForFree: true,
            publisher: {
              '@type': 'Organization',
              name: 'PennyLane',
            },
            author: family.authorNames.map((author) => ({
              '@type': 'Person',
              name: author,
            })),
          },
        ])}
      </script>
    </>
  )
}
